import { ChangeDetectionStrategy, Component, OnInit, inject } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ButtonComponent } from "@components/button/button.component";
import { DatepickerComponent } from "@components/datepicker/datepicker.component";
import { PageComponent } from "@components/page/page.component";
import { TreeSelectorComponent } from "@components/tree-selector/tree-selector.component";
import moment from "moment";
import { BusyService } from "~app/common/components/busy/busy.service";
import { SelectionItem } from "~app/common/components/tree-selector/selection-item";
import { ProduktionsmalBasdataDTO } from "~app/common/models/produktionsmal/ProduktionsmalBasdataDTO";
import { ProduktionsmalResponseDTO } from "~app/common/models/produktionsmal/ProduktionsmalResponseDTO";
import { WebinstallningarService } from "~app/common/services/webinstallningar/webinstallningar.service";
import { IValidationContext } from "~app/common/validation/IValidationContext";
import { IValidationScope } from "~app/common/validation/IValidationScope";
import { ValidationScopes } from "~app/common/validation/ValidationScopes";
import { ValidationMessageContainerComponent } from "../../../common/validation/validation-message-container/validation-message-container.component";
import { ValidationRuleDirective } from "../../../common/validation/validation-rule.directive";
import { ProduktionsmalMatrisComponent } from "./produktionsmal-matris/produktionsmal-matris.component";
import { IProduktionsmalRequest } from "./produktionsmal.models";
import { ProduktionsmalValidation } from "./produktionsmal.validation";
import { ProduktionsmalService } from "./services/produktionsmal.service";

@Component({
    selector: "rask-produktionsmal",
    templateUrl: "./produktionsmal.component.html",
    styleUrls: ["./produktionsmal.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        PageComponent,
        FormsModule,
        TreeSelectorComponent,
        DatepickerComponent,
        ValidationRuleDirective,
        ButtonComponent,
        ValidationMessageContainerComponent,
        ProduktionsmalMatrisComponent
    ]
})
export class ProduktionsmalComponent implements OnInit {
    basdata: ProduktionsmalBasdataDTO;
    hsItems: SelectionItem[] = [];
    minDate = moment(new Date()).startOf("month").toDate();
    omradenItems: SelectionItem[] = [];
    produktionsmal: ProduktionsmalResponseDTO;
    searchFilter: IProduktionsmalRequest = {
        periodstart: null,
        periodslut: null,
        handelssortimentsgruppHandelssortimentFilter: null,
        omradesFilter: null
    };
    validation: IValidationScope;
    validationContext: IValidationContext;

    private readonly busyService = inject(BusyService);
    private readonly produktionsmalService = inject(ProduktionsmalService);
    private readonly webInstallningarService = inject(WebinstallningarService);
    private readonly webSettingsKey = "Produktionsmal.searchFilter";

    clearProduktionsmalParams() {
        this.searchFilter.omradesFilter = null;
        this.searchFilter.handelssortimentsgruppHandelssortimentFilter = null;
        this.searchFilter.periodstart = null;
        this.searchFilter.periodslut = null;
        this.setupTreeSelectors();
        this.produktionsmal = null;
    }

    loadProduktionsmal() {
        if (!this.searchParamsValid()) {
            return;
        }

        this.searchFilter.handelssortimentsgruppHandelssortimentFilter = this.produktionsmalService.getSelectedHandelssortiment(this.hsItems);
        this.searchFilter.omradesFilter = this.produktionsmalService.getSelectedOmraden(this.omradenItems);
        this.webInstallningarService.set<IProduktionsmalRequest>(this.webSettingsKey, this.searchFilter);

        const request = {
            ...this.searchFilter,
            periodslut: moment(this.searchFilter.periodslut).add(1, "month").date(1).hour(0).second(0).toDate()
        };
        this.busyService.addWork(this.produktionsmalService.getProduktionsmal(request))
            .subscribe(response => {
                this.produktionsmal = response;
                // Tvättar data från '.' för att handsontable inte ska gå sönder.
                this.produktionsmal.ordrar.forEach(o => o.period = o.period.replace(".", ""));
                this.produktionsmal.ingaendeLager.forEach(o => o.period = o.period.replace(".", ""));
                this.produktionsmal.lagermal.forEach(o => o.period = o.period.replace(".", ""));
                this.produktionsmal.perioder.forEach(o => o.period = o.period.replace(".", ""));
                this.produktionsmal.produktionsmal.forEach(o => o.period = o.period.replace(".", ""));
                this.produktionsmal.handelssortimentFordelning.forEach(o => o.fordelning.forEach(x => x.period = x.period.replace(".", "")));
                this.produktionsmal.handelssortimentsgruppFordelning.forEach(o => o.fordelning.forEach(x => x.period = x.period.replace(".", "")));
            });
    }

    ngOnInit() {
        this.webInstallningarService.getAsync<IProduktionsmalRequest>(this.webSettingsKey)
            .then(p => {
                if (p) {
                    this.searchFilter = p;
                }

                this.initBasdata();
                this.initValidation();
            });
    }

    searchParamsValid = (): boolean => {
        if (!(this.searchFilter.periodslut && this.searchFilter.periodstart)) {
            return false;
        }
        return this.validationContext.validate();
    };

    private initBasdata() {
        this.produktionsmalService.getProduktionsmalBasdata()
            .subscribe(response => {
                this.basdata = response;
                this.setupTreeSelectors();
            });
    }

    private initValidation() {
        this.validationContext = ProduktionsmalValidation.init(this.searchFilter, this.minDate);
        this.validationContext.scope(ValidationScopes.sok).subscribe(v => this.validation = v);
        this.validationContext.validate();
    }

    private setupTreeSelectors() {
        this.omradenItems = this.produktionsmalService.setupOmradeTreeSelector(this.basdata.marknadsomraden, this.searchFilter.omradesFilter);
        this.hsItems = this.produktionsmalService
            .setupHandelssortimentTreeSelector(this.basdata.handelssortimentsgrupper, this.searchFilter.handelssortimentsgruppHandelssortimentFilter);
    }
}
